import herdService from '../services/herds';
import { createVoteThunks } from './voteThunks';
import { createReactionThunk } from './reactionThunks';
import mergeResultsById from '../utils/mergeResultsById';
import type { PostItem, ReduxAction, HerdPageState, VotePayload } from '../types/common';
import type { AppDispatch } from '../store';

const herdPageReducer = (
  state: HerdPageState | null = null,
  action: ReduxAction<unknown>
): HerdPageState | null => {
  switch (action.type) {
    case 'FETCH_SUB':
      return action.payload as HerdPageState;
    case 'LOAD_SUB_POSTS':
      return {
        ...state,
        posts: {
          ...((action.payload as HerdPageState).posts || {}),
          results: [
            ...(state?.posts?.results || []),
            ...(((action.payload as HerdPageState).posts?.results as unknown[]) || []),
          ],
        },
      } as HerdPageState;
    case 'REFRESH_SUB_POSTS':
      // Silent "returning" refresh: update fields of posts we already show,
      // never add/remove/reorder or touch pagination — a restored scroll
      // position must not move.
      return {
        ...state,
        posts: {
          ...(state?.posts || {}),
          results: mergeResultsById(
            (state?.posts?.results || []) as PostItem[],
            (((action.payload as HerdPageState).posts?.results as PostItem[]) || [])
          ),
        },
      } as HerdPageState;
    case 'TOGGLE_SUBPAGE_VOTE':
    case 'TOGGLE_SUBPAGE_REACTION':
      return {
        ...state,
        posts: {
          ...(state?.posts || {}),
          results: (state?.posts?.results || []).map((p) =>
            p.id !== (action.payload as VotePayload).id
              ? p
              : { ...p, ...((action.payload as VotePayload).data || {}) }
          ),
        },
      } as HerdPageState;
    case 'JOIN_HERD':
      return {
        ...state,
        herdDetails: { ...(state?.herdDetails || {}), ...(action.payload as Record<string, unknown>) },
      } as HerdPageState;
    case 'EDIT_DESCRIPTION':
      return {
        ...state,
        herdDetails: {
          ...(state?.herdDetails || {}),
          description: (action.payload as HerdPageState['herdDetails'])?.description,
          herdImage: (action.payload as HerdPageState['herdDetails'])?.herdImage,
          topics: (action.payload as any)?.topics,
        },
      } as HerdPageState;
    default:
      return state;
  }
};

export const fetchHerd = (herdName: string) => {
  return async (dispatch: AppDispatch) => {
    const herd = await herdService.getHerd(herdName, 10, 1);

    dispatch({
      type: 'FETCH_SUB',
      payload: herd,
    });
  };
};

export const loadHerdPosts = (herdName: string, page: number) => {
  return async (dispatch: AppDispatch) => {
    const herd = await herdService.getHerd(herdName, 10, page);

    dispatch({
      type: 'LOAD_SUB_POSTS',
      payload: herd,
    });
  };
};

/** Background page-1 refresh for the returning fast path (see REFRESH_SUB_POSTS). */
export const refreshHerdPosts = (herdName: string) => {
  return async (dispatch: AppDispatch) => {
    const herd = await herdService.getHerd(herdName, 10, 1);

    dispatch({
      type: 'REFRESH_SUB_POSTS',
      payload: herd,
    });
  };
};

export const { toggleUpvote, toggleDownvote } = createVoteThunks('TOGGLE_SUBPAGE_VOTE');
export const toggleReaction = createReactionThunk('TOGGLE_SUBPAGE_REACTION');

export const editDescription = (
  id: string,
  description: string,
  herdImage?: string | null,
  topics?: string[]
) => {
  return async (dispatch: AppDispatch) => {
    const updateData: Record<string, unknown> = { description };
    if (herdImage !== undefined && herdImage !== null) {
      updateData.herdImage = herdImage;
    }
    if (topics !== undefined) {
      updateData.topics = topics;
    }
    
    const updatedSub = await herdService.updateDescription(id, updateData);

    dispatch({
      type: 'EDIT_DESCRIPTION',
      payload: {
        description: updatedSub.description,
        herdImage: updatedSub.herdImage,
        topics: updatedSub.topics,
      },
    });
  };
};

export default herdPageReducer;